import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Star, ArrowRight, Quote } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReviewsPage = () => {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`${API}/reviews`);
        setReviews(response.data);
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') console.error("Error fetching reviews:", err);
      }
    };
    fetchReviews();
  }, []);

  return (
    <main data-testid="reviews-page" className="pt-20">
      {/* Hero Section */}
      <section className="section bg-[#0A0A0A]" data-testid="reviews-hero">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <span className="caption mb-4 block">Reviews & Resultaten</span>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-6 font-['Barlow_Condensed'] uppercase tracking-tighter">
              Wat Onze
              <br />
              <span className="text-red-500">Leden Zeggen</span>
            </h1>

            {/* Google Rating Badge */}
            <div className="inline-flex items-center gap-4 bg-zinc-900/50 border border-zinc-800 rounded-sm px-8 py-4 mt-8">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className="w-6 h-6 fill-red-500 text-red-500"
                  />
                ))}
              </div>
              <div className="text-left">
                <div className="text-3xl font-black text-white font-['Barlow_Condensed']">
                  4.9
                </div>
                <div className="text-xs text-zinc-500 uppercase tracking-wider">
                  Google Rating
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Review */}
      <section
        className="section bg-[#121212]"
        data-testid="featured-review"
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center relative"
          >
            <Quote className="w-16 h-16 text-red-500/20 mx-auto mb-8" />
            <blockquote className="text-2xl md:text-3xl text-white leading-relaxed mb-8 font-light italic">
              "Een top instituut om onder begeleiding je doel te bereiken. Van
              privé training tot groepslessen alles is mogelijk. De coaches
              zijn professioneel en weten je echt te motiveren."
            </blockquote>
            <div className="flex items-center justify-center gap-2 mb-2">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className="w-5 h-5 fill-red-500 text-red-500"
                />
              ))}
            </div>
            <cite className="text-red-500 font-bold not-italic">
              — Tim Bruyer
            </cite>
          </motion.div>
        </div>
      </section>

      {/* All Reviews Grid */}
      <section className="section bg-[#0A0A0A]" data-testid="reviews-grid">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-black text-white font-['Barlow_Condensed'] uppercase tracking-tighter">
              Alle <span className="text-red-500">Reviews</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((review, index) => (
              <motion.div
                key={review.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-zinc-900/50 border border-zinc-800 p-8 rounded-sm hover:border-red-500/30 transition-all duration-500"
                data-testid={`review-${review.id}`}
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5 fill-red-500 text-red-500"
                    />
                  ))}
                </div>
                <p className="text-zinc-300 mb-6 italic leading-relaxed">
                  "{review.tekst}"
                </p>
                <p className="text-red-500 font-bold text-sm">
                  — {review.naam}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="section bg-[#121212]" data-testid="results-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="caption mb-4 block">Resultaten</span>
            <h2 className="text-4xl md:text-5xl font-black text-white font-['Barlow_Condensed'] uppercase tracking-tighter">
              Bewezen <span className="text-red-500">Succes</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: "500+", label: "Tevreden Leden" },
              { number: "4.9", label: "Google Rating" },
              { number: "10+", label: "Jaar Ervaring" },
              { number: "1000+", label: "Transformaties" },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
                data-testid={`stat-${index}`}
              >
                <div className="text-5xl md:text-6xl font-black text-red-500 font-['Barlow_Condensed'] mb-2">
                  {stat.number}
                </div>
                <div className="text-zinc-500 text-sm uppercase tracking-wider">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-[#0A0A0A]" data-testid="reviews-cta">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="caption mb-4 block">Word Ons Volgende Succes</span>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6 font-['Barlow_Condensed'] uppercase tracking-tighter">
              Jouw Verhaal <span className="text-red-500">Begint Hier</span>
            </h2>
            <p className="text-zinc-400 text-lg mb-10">
              Sluit je aan bij honderden tevreden leden en ontdek wat Arena Gym voor
              jou kan betekenen.
            </p>
            <Link
              to="/boeken"
              data-testid="reviews-book-cta"
              className="btn-primary inline-flex items-center gap-2"
            >
              Start Jouw Transformatie
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default ReviewsPage;
